<template>
  <div class="asc-filters">
    <slot name="header"></slot>
    <div class="asc-filters--wrapper">
      <div class="asc-filters--fields">
        <template v-for="field in getFields">
          <template v-if="!field.isDate && field.visible">
            <asc-tooltip
              :key="field.id"
              :tooltip="tooltip"
              bottom
              :tooltip-length="0"
            >
              <asc-autocomplete-selector
                :id="field.id"
                v-model="field.selection"
                :class="[
                  'asc-child-option',
                  !dense || field.wide ? 'asc-child-option-wide' : '',
                  field.extraWide ? 'asc-child-option-extra-wide' : '',
                ]"
                :component-type="getComponentType(field.multiple)"
                :display-clear="getDisplayClear(field.multiple)"
                :no-placeholder="noPlaceholder"
                :items="getLookups(field)"
                :locked-items="field.lockedItems"
                :label="field.label"
                :on-change="handleSelectorChange"
                :placeholder="field.placeholder"
                :readonly="readonly || field.selectorDisabled"
                :required="field.validationCount > 0"
                :display-error-message="
                  displayErrorMessage && field.validationCount > 0
                "
                validation-error-message="All required fields must be completed to apply filters."
                :selected-items="field.selectedItems"
                :sort-items="field.sortItems"
                :disabled="disabled"
              ></asc-autocomplete-selector>
            </asc-tooltip>
          </template>
          <template
            v-if="field.isDate && field.showFixedOptions && field.visible"
          >
            <asc-tooltip
              :key="field.id"
              :tooltip="tooltip"
              bottom
              :tooltip-length="0"
            >
              <asc-date-picker
                :id="field.id"
                :key="field.id"
                v-model="field.selection"
                :label="field.label"
                :max-days="field.maxDays"
                :show-fixed-options="field.showFixedOptions"
                :hide-options-with-id="field.hideOptionsWithId"
                :has-icon="field.dropDown ? true : false"
                :drop-down="field.dropDown ? field.dropDown : false"
                :readonly="readonly"
                auto-re-position
                hide-details
                :class="[
                  'asc-child-option',
                  !dense ? 'asc-child-option-wide' : '',
                ]"
                :disabled="disabled"
                @input:id="handleSelectorChange"
              ></asc-date-picker>
            </asc-tooltip>
          </template>
          <template
            v-if="field.isDate && !field.showFixedOptions && field.visible"
          >
            <asc-tooltip
              :key="field.id"
              :tooltip="tooltip"
              bottom
              :tooltip-length="0"
            >
              <asc-date-picker
                :id="field.id"
                class="asc-filters--date-picker"
                :value="field.selection"
                :label="field.label"
                :readonly="readonly"
                :has-icon="true"
                :disabled="disabled"
                format="DD MMM YYYY"
                @input:id="handleDatePickerChange"
              ></asc-date-picker>
            </asc-tooltip>
          </template>
        </template>
        <template
          v-if="submitButtonVisible && !readonly && getFields.length > 0"
        >
          <asc-accent-button
            :disabled="isSubmitDisabled"
            :button-text="buttonText"
            secondary
            :on-click="handleSubmit"
          ></asc-accent-button>
        </template>
      </div>
      <div class="asc-filters--actions">
        <slot name="actions"></slot>
      </div>
    </div>
  </div>
</template>

<script>
import AscAutocompleteSelector from './AscAutocompleteSelector'
import AscDatePicker from '@/components/atoms/AscDatePicker'
import AscAccentButton from '@/components/atoms/AscAccentButton'
import AscTooltip from '@/components/atoms/AscTooltip'

export default {
  name: 'AscFilters',

  components: {
    AscAutocompleteSelector,
    AscDatePicker,
    AscAccentButton,
    AscTooltip,
  },

  props: {
    id: {
      type: String,
      required: true,
    },
    lookups: {
      type: Array,
      default: () => [],
    },
    fields: {
      type: Array,
      default: () => [],
    },
    buttonText: {
      type: String,
      default: 'Submit',
    },
    dense: {
      type: Boolean,
      default: true,
    },
    displayErrorMessage: {
      type: Boolean,
      default: false,
    },
    noPlaceholder: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    submitButtonVisible: {
      type: Boolean,
      default: true,
    },
    tooltip: {
      type: String,
      default: '',
    },
    onSubmit: {
      type: Function,
      default: () => {},
    },
    onDisableSubmit: {
      type: Function,
      default: () => {},
    },
    onFiltersChange: {
      type: Function,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      localFields: this.fields,
      oldSelections: this.updateSelections(this.fields),
      submitDisabled: true,
    }
  },

  computed: {
    getFields() {
      return this.fields
    },

    getSelections() {
      return this.localFields.map(item => {
        return {
          id: item.id,
          selection: item.selection || [],
          selectedItems: item.selectedItems || [],
        }
      })
    },

    isSubmitDisabled() {
      let result = false

      if (this.lookups.length === 0 || this.localFields.length === 0) {
        return true
      }

      this.localFields.forEach(field => {
        const selectionsField = this.getFieldGivenId(field.id)
        if (selectionsField && selectionsField.selection) {
          if (field.validationCount > selectionsField.selection.length) {
            result = true
          }
        }
      })

      if (result === true && this.submitDisabled === false) {
        this.handleDisableSubmit()
      }
      return result
    },
  },

  watch: {
    fields(newValue) {
      this.localFields = newValue
    },
    lookups() {
      this.initSelections()
    },
  },

  mounted() {
    this.initSelections()
  },

  methods: {
    initSelections() {
      if (!this.readonly && this.lookups.length > 0) {
        this.localFields.forEach(item => {
          this.handleSelectionUpdate(
            item.id,
            item.selection,
            item.selectedItems,
          )
        })
      }
    },

    getComponentType(isMultiple) {
      return isMultiple ? 'multiple' : 'single'
    },

    getDisplayClear(isMultiple) {
      return !isMultiple
    },

    getFieldLookupsGivenId(id) {
      const [field] = this.lookups.filter(item => item.id === id)
      return (field && field.items) || []
    },

    getFieldGivenId(id) {
      const [field] = this.localFields.filter(item => item.id === id)
      return field || null
    },

    isItemValid(item, currentField, selectedItems) {
      if (currentField.lookupFields.length === 1) {
        const field1 = currentField.lookupFields[0]
        let items = selectedItems
        if (items.length === 1 && items[0] === undefined) {
          items = []
        }
        const filteredItems = items.filter(
          record => record[field1] === item[field1],
        )
        return filteredItems.length > 0
      }
      if (currentField.lookupFields.length === 2) {
        const field1 = currentField.lookupFields[0]
        const field2 = currentField.lookupFields[1]
        let items = selectedItems
        if (items.length === 1 && items[0] === undefined) {
          items = []
        }
        const filteredItems = items.filter(
          record =>
            record[field1] === item[field1] && record[field2] === item[field2],
        )
        return filteredItems.length > 0
      }
      return true
    },

    isArrayItemValid(item, currentField, selectedItems) {
      if (!currentField.lookupArrayFields) {
        return true
      }
      if (currentField.lookupArrayFields.length === 1) {
        const lookup1 = currentField.lookupArrayFields[0]
        const arrayLookup = lookup1[0]
        const fieldLookup = lookup1[1]
        let items = selectedItems
        if (items.length === 1 && items[0] === undefined) {
          items = []
        }
        const filteredItems = items.filter(record =>
          record[arrayLookup].includes(item[fieldLookup]),
        )
        return filteredItems.length > 0
      }
      return false
    },

    getLookups(field) {
      // get field information
      const fieldLookups = this.getFieldLookupsGivenId(field.id)
      const currentField = this.getFieldGivenId(field.id)
      const currentFieldLookups = this.getFieldLookupsGivenId(field.id)
      const selectedItems = currentField.selectedItems || []

      if (!this.readonly && this.lookups.length > 0) {
        // remove invalid selections from current field
        if (field.id) {
          const currentSelectionCount = currentField.selection.length
          const currentFieldSelection = currentField.selection.filter(
            item =>
              currentFieldLookups.filter(item2 => item2.value === item).length >
              0,
          )
          const updatedCurrentSelectionCount = currentFieldSelection.length
          const currentFieldSelectedItems = currentField.selection.filter(
            item =>
              currentFieldLookups.filter(item2 => item2.value === item.value)
                .length > 0,
          )

          // if there are changes, update selection
          if (currentSelectionCount !== updatedCurrentSelectionCount) {
            this.handleSelectionUpdate(
              field.id,
              currentFieldSelection,
              currentFieldSelectedItems,
            )
          }
        }

        // update current field based on parent field selections
        if (field.parentId && (field.lookupFields || field.lookupArrayFields)) {
          let filteredLookups = []

          // get parent field and parent selected items
          const parentField = this.getFieldGivenId(field.parentId)
          const parentSelectedItems = parentField.selectedItems || []

          // verify if any current selections are invalid
          const filteredSelectedItems = selectedItems.filter(
            item =>
              this.isItemValid(item, currentField, parentSelectedItems) &&
              this.isArrayItemValid(item, currentField, parentSelectedItems),
          )

          if (
            selectedItems.length > 0 &&
            selectedItems.length !== filteredSelectedItems.length &&
            filteredSelectedItems.length === 0
          ) {
            this.handleSelectionUpdate(field.id, [], [])
          }

          // filter current field lookup items given parent selected items
          if (parentSelectedItems.length > 0) {
            filteredLookups = currentFieldLookups.filter(
              item =>
                this.isItemValid(item, currentField, parentSelectedItems) &&
                this.isArrayItemValid(item, currentField, parentSelectedItems),
            )

            // Filter derived params based on selected airlineId
            if (
              field.hasDerivedParams &&
              field.id === 'parameter' &&
              field.parentId === 'component'
            ) {
              // If parent/Component is already has custom paramter selected
              if (
                parentSelectedItems.length &&
                parentSelectedItems[0]?.value === 'custom_parameter'
              ) {
                const customParameterParentField = this.getFieldGivenId(
                  field.customParameterParentId,
                )
                const customParameterParentSelectedItems =
                  customParameterParentField.selectedItems || []

                // Filter again based on the airline selected
                if (customParameterParentSelectedItems.length) {
                  filteredLookups = filteredLookups.filter(
                    item =>
                      item.airlineId ===
                      customParameterParentSelectedItems[0].airlineId,
                  )

                  if (field.selectedItems.length) {
                    // If already derived parameter is selected then
                    // check if that exist in the lookups
                    if (filteredLookups.length) {
                      // If there are any lookup data available then
                      // search for the current selected item in the lookup array
                      const foundAlreadySelectedparam = filteredLookups.find(
                        filteredParams =>
                          filteredParams.parameterId ===
                          field.selectedItems[0].parameterId,
                      )

                      if (foundAlreadySelectedparam === undefined) {
                        // Clear selections if the current selected parameter doesnt exists in the lookup
                        this.handleSelectionUpdate(field.id, [], [])
                        this.handleSelectionUpdate(field.parentId, [], [])
                        this.handleSelectionUpdate(parentField.parentId, [], [])
                      }
                    } else {
                      // Clear selected parameter if the lookups array is empty
                      this.handleSelectionUpdate(field.id, [], [])
                      this.handleSelectionUpdate(field.parentId, [], [])
                      this.handleSelectionUpdate(parentField.parentId, [], [])
                    }
                  }
                }
              }
            }

            // If the currentField selection is zero and
            // If parentField has a selection done and
            // if the currentfield lookup is having only one item then autoselect the current item for the currentField
            if (
              currentField.selection.length === 0 &&
              parentField.selection.length
            ) {
              if (
                filteredLookups.length === 1 ||
                (currentField.id === 'component' &&
                  parentField.selection[0] === 'custom_parameter')
              ) {
                this.handleSelectionUpdate(
                  currentField.id,
                  [filteredLookups[0].value],
                  [filteredLookups[0]],
                )
              }
            }
          }

          // if current field has selections but parent doesn't, clear current field selections
          if (
            currentField.selection.length > 0 &&
            parentField.selection.length === 0
          ) {
            this.handleSelectionUpdate(field.id, [], [])
          }

          return filteredLookups
        }
      }

      return fieldLookups
    },

    getSelectedItemsGivenSelection(id, selection, selectedItems) {
      const fieldLookups = this.getFieldLookupsGivenId(id)
      if (selection.length > 0 && selectedItems && selectedItems.length === 0) {
        return fieldLookups
      }
      if (selection.length > 0 && selectedItems && selectedItems.length > 0) {
        return selectedItems
      }
      return []
    },

    updateSelections(fields) {
      return fields.map(item => {
        return {
          id: item.id,
          isDate: item.isDate,
          selection: item.selection,
        }
      })
    },

    handleSelectionUpdate(id, selection, selectedItems) {
      const field = this.getFieldGivenId(id)
      if (!field.isDate) {
        const [original] = this.oldSelections.filter(item => item.id === id)
        const newSelection = Array.isArray(selection) ? selection : []
        const originalString = original
          ? original.selection.sort().toString()
          : ''
        const newString = newSelection.sort().toString()

        if (originalString !== newString) {
          field.selection = selection
          field.selectedItems = this.getSelectedItemsGivenSelection(
            id,
            selection,
            selectedItems,
          )
          this.localFields.filter(item => item.id !== id).push(field)
          this.oldSelections = this.updateSelections(this.localFields)
          this.onFiltersChange(this.id || 0)
        }
      }

      if (field.isDate) {
        const [original] = this.oldSelections.filter(item => item.id === id)
        const originalSelection = original ? original.selection : {}
        const newSelection = selection
        const originalDates = originalSelection.dates || {}
        const newDates = newSelection.dates || {}

        const originalDatesStartDate = new Date(originalDates.startDate)
        originalDatesStartDate.setHours(0, 0, 0, 0)
        const newDatesStartDate = new Date(newDates.startDate)
        newDatesStartDate.setHours(0, 0, 0, 0)
        const originalDatesEndDate = new Date(originalDates.endDate)
        originalDatesEndDate.setHours(0, 0, 0, 0)
        const newDatesEndDate = new Date(newDates.endDate)
        newDatesEndDate.setHours(0, 0, 0, 0)

        if (
          originalDates.value !== '0' &&
          newDates.value !== '0' &&
          (originalDatesStartDate.getTime() !== newDatesStartDate.getTime() ||
            originalDatesEndDate.getTime() !== newDatesEndDate.getTime() ||
            originalDates.value !== newDates.value)
        ) {
          field.selection = selection
          field.selectedItems = this.getSelectedItemsGivenSelection(
            id,
            selection,
            selectedItems,
          )
          this.localFields.filter(item => item.id !== id).push(field)
          this.oldSelections = this.updateSelections(this.localFields)
          if (this.submitButtonVisible) this.onFiltersChange(this.id || 0)
          else this.onFiltersChange(this.id || 0, this.getSelections)
        }
      }
    },

    handleSelectorChange(id, selection, selectedItems) {
      this.handleSelectionUpdate(id, selection, selectedItems)
    },

    handleDatePickerChange(id, selection) {
      this.handleSelectionUpdate(id, selection, [])
    },

    handleSubmit() {
      if (this.onSubmit) {
        this.onSubmit(this.id, this.getSelections)
      }
    },

    handleDisableSubmit() {
      this.submitDisabled = true
      if (this.onDisableSubmit) {
        this.onDisableSubmit(this.id, this.getSelections)
      }
    },
  },
}
</script>

<style lang="scss">
.asc-filters {
  &--wrapper {
    display: flex;
    flex-wrap: wrap;

    .asc-child-option {
      width: 150px !important;
      margin: 8px 16px 0 0;

      &.asc-datepicker-input--container {
        width: 175px !important;

        &.asc-child-option-wide {
          width: 200px !important;
        }
      }
    }

    .asc-child-option-wide {
      width: 200px !important;
    }

    .asc-child-option-extra-wide {
      width: 250px !important;
    }

    .asc-filters--fields {
      display: flex;
      flex: 1;
      flex-wrap: wrap;
      margin-right: 16px;

      .asc-accent-button-container {
        margin-top: 20px;
      }
    }

    .asc-filters--actions {
      margin-top: auto;
      justify-content: flex-end;
      margin-right: 12px;
    }

    .asc-filters--date-picker {
      margin-top: 8px;
      padding-right: 16px;
    }
  }
}
</style>
